import { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, onSnapshot } from "firebase/firestore";

import Banner from "../components/Banner";
import Banner2 from "../components/Banner2";
import Banner3 from "../components/Banner3";
import ProductCard from "../components/ProductCard";
import CategoryCard from "../components/CategoryCard";
import FAQ from "../components/faq";

import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/autoplay";

import { FreeMode, Navigation, Pagination, Autoplay } from "swiper/modules";

import "../styles/Home.css";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  const OFFER_CID = 1781340284731;
  const SPECIAL_CID = 1781353394225;

  // 🔧 FIX: use state (not refs) for custom nav elements.
  // With refs, Swiper's `onBeforeInit` often runs before the DOM node
  // is attached (especially since these Swipers only mount after
  // isLoading flips), so navigation.prevEl/nextEl stay null forever.
  // State forces a re-render once the node exists, and Swiper's React
  // bindings pick up the change automatically.
  const [offerPrevEl, setOfferPrevEl] = useState(null);
  const [offerNextEl, setOfferNextEl] = useState(null);

  const [specialPrevEl, setSpecialPrevEl] = useState(null);
  const [specialNextEl, setSpecialNextEl] = useState(null);

  // 🔥 FETCH PRODUCTS
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "products"), (snap) => {
      const data = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProducts(data);
      setIsLoading(false);
    });

    return () => unsub();
  }, []);

  // 🔥 FETCH CATEGORIES
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "categories"), (snap) => {
      const data = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(data);
      setCategoriesLoading(false);
    });

    return () => unsub();
  }, []);

  // 🔥 FILTER PRODUCTS
  const offerProducts = products.filter(
    (product) => Number(product.categoryId) === OFFER_CID
  );

  const specialProducts = products.filter(
    (product) => Number(product.categoryId) === SPECIAL_CID
  );

  return (
    <div className="home__wrapper">
      <Banner />

      {/* 🔥 OFFER ZONE SECTION */}
      <section className="home__section offer-section">
        <div className="home__section-container">
          <h2 className="home__section-title">
            <span className="home__title-icon">🔥</span>
            Offer Zone
          </h2>

          {!isLoading && offerProducts.length > 0 && (
            <>
              <div
                ref={setOfferPrevEl}
                className="banner-arrow banner-arrow--left home-arrow"
              >
                <span>❮</span>
              </div>

              <div
                ref={setOfferNextEl}
                className="banner-arrow banner-arrow--right home-arrow"
              >
                <span>❯</span>
              </div>
            </>
          )}

          {!isLoading && offerProducts.length > 0 ? (
            <Swiper
              modules={[Navigation, Pagination]}
              navigation={{
                prevEl: offerPrevEl,
                nextEl: offerNextEl,
              }}
              pagination={{ clickable: true }}
              grabCursor={true}
              className="home__products-swiper"
              breakpoints={{
                320: {
                  slidesPerView: 2,
                  spaceBetween: 10,
                },
                640: {
                  slidesPerView: 2,
                  spaceBetween: 15,
                },
                768: {
                  slidesPerView: 3,
                  spaceBetween: 18,
                },
                1024: {
                  slidesPerView: 4,
                  spaceBetween: 20,
                },
              }}
            >
              {offerProducts.map((product) => (
                <SwiperSlide key={product.id}>
                  <ProductCard product={product} />
                </SwiperSlide>
              ))}
            </Swiper>
          ) : isLoading ? (
            <div className="home__loading">Loading offers...</div>
          ) : (
            <p className="home__empty-state">No offers available right now</p>
          )}
        </div>
      </section>

      {/* ⭐ SPECIAL PICKS SECTION */}
      <section className="home__section special-section">
        <div className="home__section-container">
          <h2 className="home__section-title">
            <span className="home__title-icon">🌙</span>
            Customized Gifts
          </h2>

          {!isLoading && specialProducts.length > 0 && (
            <>
              <div
                ref={setSpecialPrevEl}
                className="banner-arrow banner-arrow--left home-arrow"
              >
                <span>❮</span>
              </div>

              <div
                ref={setSpecialNextEl}
                className="banner-arrow banner-arrow--right home-arrow"
              >
                <span>❯</span>
              </div>
            </>
          )}

          {!isLoading && specialProducts.length > 0 ? (
            <Swiper
              modules={[Navigation, Pagination]}
              navigation={{
                prevEl: specialPrevEl,
                nextEl: specialNextEl,
              }}
              pagination={{ clickable: true }}
              grabCursor={true}
              className="home__products-swiper"
              breakpoints={{
                320: {
                  slidesPerView: 2,
                  spaceBetween: 10,
                },
                640: {
                  slidesPerView: 2,
                  spaceBetween: 15,
                },
                768: {
                  slidesPerView: 3,
                  spaceBetween: 18,
                },
                1024: {
                  slidesPerView: 4,
                  spaceBetween: 20,
                },
              }}
            >
              {specialProducts.map((product) => (
                <SwiperSlide key={product.id}>
                  <ProductCard product={product} />
                </SwiperSlide>
              ))}
            </Swiper>
          ) : isLoading ? (
            <div className="home__loading">Loading special picks...</div>
          ) : (
            <p className="home__empty-state">No special products available</p>
          )}
        </div>
      </section>

      <Banner2 />

      {/* 🎁 CATEGORY SLIDER SECTION */}
      <section className="home__section category-section">
        <div className="home__section-container">
          <h2 className="home__section-title">
            <span className="home__title-icon">🎁</span>
            Our Catalog
          </h2>

          {categoriesLoading ? (
            <div className="home__loading">Loading catalog...</div>
          ) : categories.length > 0 ? (
            <Swiper
              modules={[FreeMode, Navigation, Pagination, Autoplay]}
              slidesPerView={2}
              spaceBetween={16}
              freeMode={true}
              navigation={true}
              pagination={{ clickable: true }}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              grabCursor={true}
              className="home__categories-swiper"
              breakpoints={{
                480: {
                  slidesPerView: 2,
                  spaceBetween: 12,
                },
                640: {
                  slidesPerView: 3,
                  spaceBetween: 14,
                },
                768: {
                  slidesPerView: 4,
                  spaceBetween: 16,
                },
                1024: {
                  slidesPerView: 5,
                  spaceBetween: 18,
                },
                1440: {
                  slidesPerView: 6,
                  spaceBetween: 20,
                },
              }}
            >
              {categories.map((category) => (
                <SwiperSlide key={category.id}>
                  <CategoryCard category={category} />
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className="home__empty-state">No categories found</div>
          )}
        </div>
      </section>

      <Banner3 />

      <FAQ />
    </div>
  );
}